import React, { useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import {
  CURRENCY,
  TABLE_ITEMS,
  TOTAL,
  WAFFY_FEES,
  NO_DISCOUNT_MESSAGE,
  DISCOUNT,
  CODE,
  DISCOUNT_AMOUNT,
  TABBY_FEES,
  NEO_FEES,
  TAMARA_FEES,
} from "./constants";
import { IContractProps } from "../../models";

// styles
import styles from "./styles.module.css";
import classNames from "classnames";
import {
  ETrasactionsType,
  IContractActionsResponseData,
  voucherContent,
} from "../../../../setup/api/services/contract/models";
import { useMemo } from "react";
import { EContractActions } from "../../../../setup/api/services/contractActions/enum";
import { getShipmentFeesTotal } from "../../../../setup/util/contract";
import { DeliveryLabel } from "./deliveryLabel";
import DeliveryInfoModal from "./deliveryInfoModal";

export interface Props extends IContractProps {
  voucherData?: voucherContent;
  contractActions?: IContractActionsResponseData[];
}

const BuyerPaymentDetails = ({
  voucherData,
  contract,
  contractActions,
}: Props) => {
  const [showDeliveryInfoModal, setShowDeliveryInfoModal] = useState(false);

  function createRow(desc: string, price: number) {
    return { desc, price };
  }

  const subTotal = useMemo(() => {
    const netPrice = contract?.hasVoucher
      ? contract?.originalItemPrice || contract?.itemPrice
      : contract?.itemPrice;
    return netPrice + getShipmentFeesTotal(contract);
  }, [contract]);

  const tabbyAllowed = useMemo(() => {
    return contract?.transactions?.some(
      (transaction) =>
        transaction?.type === ETrasactionsType.CUSTOMER_PAY &&
        transaction?.paymentMethod === "TABBY"
    );
  }, [contract]);

  const neoAllowed = useMemo(() => {
    return contract?.transactions?.some(
      (transaction) =>
        transaction?.type === ETrasactionsType.CUSTOMER_PAY &&
        transaction?.paymentMethod === "NEO"
    );
  }, [contract]);

  const tamaraAllowed = useMemo(() => {
    return contract?.transactions?.some(
      (transaction) =>
        transaction?.type === ETrasactionsType.CUSTOMER_PAY &&
        transaction?.paymentMethod === "TAMARA"
    );
  }, [contract]);

  const isAdminAction = useMemo(() => {
    if (!contractActions?.length) return false;
    if (contractActions?.length <= 0) return false;
    if (
      [
        EContractActions.ADMIN_ESCALATE_DELIVERY_FOR_CUSTOMER,
        EContractActions.ADMIN_ESCALATE_ITEM_FOR_CUSTOMER,
        EContractActions.ADMIN_RESOLVE_ESCALATION_REFUND_CUSTOMER,
        EContractActions.ADMIN_CLOSE_REFUND_CUSTOMER,
      ].includes(
        contractActions[contractActions?.length - 1]
          ?.action as unknown as EContractActions
      )
    ) {
      return true;
    } else {
      return false;
    }
  }, [contractActions]);

  const total = useMemo(() => {
    if (isAdminAction) {
      return (
        subTotal -
        (voucherData?.discountAmount || 0) -
        getShipmentFeesTotal(contract)
      );
    } else {
      return subTotal - (voucherData?.discountAmount || 0);
    }
  }, [contract, isAdminAction, subTotal, voucherData?.discountAmount]);

  const rows = [
    createRow(
      TABLE_ITEMS.NET_PRICE,
      contract?.hasVoucher
        ? contract?.originalItemPrice || contract?.itemPrice
        : contract?.itemPrice || 0
    ),
    createRow(TABLE_ITEMS.DELIVERY_FEES, getShipmentFeesTotal(contract)),
    createRow(TABLE_ITEMS.SUB_TOTAL, subTotal || 0),
  ];

  return (
    <TableContainer className={styles.container}>
      <DeliveryInfoModal
        contract={contract}
        open={showDeliveryInfoModal}
        onClose={() => setShowDeliveryInfoModal(false)}
      />
      <Table aria-label="spanning table">
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={row.desc}>
              <TableCell
                className={classNames(
                  index + 1 !== rows.length
                    ? styles.table_cell
                    : styles.table_cell_strong,
                  index + 1 !== rows.length && styles.desc,
                  index + 1 === rows.length && styles.divider
                )}>
                {row.desc === TABLE_ITEMS.DELIVERY_FEES && contract?.shippable ? (
                  <DeliveryLabel
                    onClick={() => setShowDeliveryInfoModal(true)}
                  />
                ) : (
                  row.desc
                )}
              </TableCell>
              <TableCell
                className={classNames(
                  index + 1 !== rows.length
                    ? styles.table_cell
                    : styles.price_subtotal,
                  index + 1 !== rows.length && styles.price,
                  index + 1 === rows.length && styles.divider
                )}
                align="right">
                {row.price?.toFixed(2)}{" "}
                <span className={styles.currency}>{CURRENCY}</span>
              </TableCell>
            </TableRow>
          ))}

          {isAdminAction && (
            <TableRow>
              <TableCell className={classNames(styles.table_cell, styles.desc)}>
                {tabbyAllowed
                  ? TABBY_FEES
                  : neoAllowed
                  ? NEO_FEES
                  : tamaraAllowed
                  ? TAMARA_FEES
                  : WAFFY_FEES}
              </TableCell>
              <TableCell
                className={classNames(styles.table_cell, styles.price)}
                align="right">
                {"-"}
                {contract?.fee?.toFixed(2)}
                <span className={styles.currency}>{CURRENCY}</span>
              </TableCell>
            </TableRow>
          )}

          <TableRow>
            <TableCell className={classNames(styles.table_cell, styles.desc)}>
              {DISCOUNT_AMOUNT}
            </TableCell>
            <TableCell
              className={classNames(styles.table_cell, styles.price)}
              align="right">
              {"-"}
              {voucherData?.discountAmount?.toFixed(2) || 0}
              <span className={styles.currency}>{CURRENCY}</span>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <div
        className={classNames(
          contract?.hasVoucher
            ? styles["success_pill"]
            : styles["disabled_pill"],
          styles["mb1"]
        )}>
        <p
          className={classNames(
            styles["footnote"],
            contract?.hasVoucher && styles["doneText"]
          )}>
          {contract?.hasVoucher
            ? `${DISCOUNT},${voucherData?.discountInfo?.percentOff}% ${CODE} (${voucherData?.voucherCode})`
            : `${NO_DISCOUNT_MESSAGE}`}
        </p>
      </div>

      <Table aria-label="spanning table">
        <TableBody>
          <TableRow>
            <TableCell className={classNames(styles.table_cell, styles.total)}>
              {TOTAL}
            </TableCell>
            <TableCell
              className={classNames(
                styles.table_cell,
                styles.price_total,
                styles.doneText
              )}
              align="right">
              {total?.toFixed(2)}{" "}
              <span className={styles.currency}>{CURRENCY}</span>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default BuyerPaymentDetails;
